"use client";

import { controlSurfaceVariants } from "@/components/ui/control-surface";
import { headingVariants } from "@/components/ui/Heading";
import { Label } from "@/components/ui/Label";
import { textVariants } from "@/components/ui/Text";
import { Slot } from "@radix-ui/react-slot";
import * as React from "react";
import cn from "twsx";

type FieldOrientation = "vertical" | "horizontal" | "inline";

type FieldContextValue = {
  id: string;
  controlId: string;
  descriptionId: string;
  messageId: string;
  invalid: boolean;
  incomplete: boolean;
  disabled: boolean;
  required: boolean;
  orientation: FieldOrientation;
};

const FieldContext = React.createContext<FieldContextValue | null>(null);

function mergeAriaIds(...ids: Array<string | undefined>) {
  const value = ids.filter(Boolean).join(" ").trim();
  return value.length > 0 ? value : undefined;
}

export function useField() {
  const context = React.useContext(FieldContext);

  if (!context) {
    throw new Error("useField must be used within <Field>");
  }

  return context;
}

type FieldProps = React.HTMLAttributes<HTMLDivElement> & {
  variant?: "default" | "card";
  invalid?: boolean;
  incomplete?: boolean;
  disabled?: boolean;
  required?: boolean;
  orientation?: FieldOrientation;
};

// `card` sets a field on its own bordered surface, for acknowledgements and
// read-only notes that sit between ordinary fields. `incomplete` marks only the
// control, for a required answer that is still missing; the label keeps its colour.
/** Use when laying out any form control with its label, description, count and message. */
const Field = ({
  className,
  variant = "default",
  id: providedId,
  invalid = false,
  incomplete = false,
  disabled = false,
  required = false,
  orientation = "vertical",
  ref,
  ...props
}: FieldProps & { ref?: React.Ref<HTMLDivElement> }) => {
  const reactId = React.useId();
  const id = providedId ?? reactId;

  const value = React.useMemo<FieldContextValue>(() => ({
    id,
    controlId: `${id}-control`,
    descriptionId: `${id}-description`,
    messageId: `${id}-message`,
    invalid,
    incomplete,
    disabled,
    required,
    orientation,
  }), [id, invalid, incomplete, disabled, required, orientation]);

  return (
    <FieldContext.Provider value={value}>
      <div
        ref={ref}
        data-slot="field"
        data-invalid={invalid ? "true" : undefined}
        data-disabled={disabled ? "true" : undefined}
        data-orientation={orientation}
        className={cn(
          "flex min-w-0 flex-col gap-sm",
          variant === "card" && "rounded-control border border-border-primary bg-bg-surface p-4 shadow-low",
          orientation === "horizontal" && "sm:flex-row sm:items-start sm:justify-between",
          orientation === "inline" && "flex-row items-center justify-between",
          className,
        )}
        {...props}
      />
    </FieldContext.Provider>
  );
};

const FieldLabel = ({
  htmlFor,
  isRequired,
  variant,
  ref,
  ...props
}: React.ComponentProps<typeof Label> & { ref?: React.Ref<React.ElementRef<typeof Label>> }) => {
  const field = useField();

  return (
    <Label
      ref={ref}
      htmlFor={htmlFor ?? field.controlId}
      isRequired={isRequired ?? field.required}
      variant={field.invalid ? "invalid" : variant}
      {...props}
    />
  );
};
FieldLabel.displayName = "FieldLabel";

const FieldTitle = ({
  className,
  ref,
  ...props
}: React.HTMLAttributes<HTMLHeadingElement> & { ref?: React.Ref<HTMLHeadingElement> }) => (
  <h4
    ref={ref}
    className={cn(headingVariants({ size: "sm", weight: "medium" }), className)}
    {...props}
  />
);
FieldTitle.displayName = "FieldTitle";

const FieldDescription = ({
  className,
  id,
  ref,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & { ref?: React.Ref<HTMLParagraphElement> }) => {
  const field = useField();

  return (
    <p
      ref={ref}
      id={id ?? field.descriptionId}
      className={cn(textVariants({ size: "sm", tone: "tertiary" }), className)}
      {...props}
    />
  );
};
FieldDescription.displayName = "FieldDescription";

const FieldControl = ({
  id,
  ref,
  ...props
}: React.ComponentProps<typeof Slot> & { ref?: React.Ref<HTMLElement> }) => {
  const field = useField();
  const describedBy = mergeAriaIds(
    props["aria-describedby"],
    field.descriptionId,
    field.invalid ? field.messageId : undefined,
  );

  return (
    <Slot
      ref={ref}
      id={id ?? field.controlId}
      aria-describedby={describedBy}
      aria-invalid={field.invalid || field.incomplete}
      data-invalid={field.invalid || field.incomplete ? "true" : undefined}
      data-disabled={field.disabled ? "true" : undefined}
      {...props}
    />
  );
};
FieldControl.displayName = "FieldControl";

// Read-only value drawn on the field surface, for disabled pickers that show
// their saved answer as text.
const FieldValue = ({
  className,
  children,
  isPlaceholder = false,
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & { isPlaceholder?: boolean; ref?: React.Ref<HTMLDivElement> }) => (
  <div
    ref={ref}
    className={cn(
      controlSurfaceVariants({ size: "md" }),
      "flex items-center",
      isPlaceholder && "text-text-tertiary",
      className,
    )}
    {...props}
  >
    <span className="truncate">{children}</span>
  </div>
);
FieldValue.displayName = "FieldValue";

// Character count under a text control. It shows while the control is focused
// or filled and turns warning, then error, as the count nears its limit.
const FieldCount = ({
  className,
  visible,
  tone = "default",
  ref,
  ...props
}: React.HTMLAttributes<HTMLDivElement> & {
  visible: boolean;
  tone?: "default" | "warning" | "error";
  ref?: React.Ref<HTMLDivElement>;
}) => (
  <div
    ref={ref}
    aria-hidden
    className={cn(
      textVariants({ size: "sm", tone: "inherit", numeric: true }),
      "mt-1 text-right",
      visible ? "opacity-100" : "opacity-0",
      tone === "default" && "text-text-tertiary",
      tone === "warning" && "text-yellow-text",
      tone === "error" && "text-red-text",
      className,
    )}
    {...props}
  />
);
FieldCount.displayName = "FieldCount";

const FieldMessage = ({
  className,
  children,
  id,
  tone = "error",
  ref,
  ...props
}: React.HTMLAttributes<HTMLParagraphElement> & {
  tone?: "error" | "muted";
  ref?: React.Ref<HTMLParagraphElement>;
}) => {
  const field = React.useContext(FieldContext);

  if (!children) {
    return null;
  }

  return (
    <p
      ref={ref}
      id={id ?? field?.messageId}
      role={tone === "error" ? "alert" : undefined}
      className={cn(
        textVariants(tone === "error" ? { size: "sm", weight: "medium", tone: "danger" } : { size: "sm", tone: "tertiary" }),
        className,
      )}
      {...props}
    >
      {children}
    </p>
  );
};
FieldMessage.displayName = "FieldMessage";

export { Field, FieldControl, FieldCount, FieldDescription, FieldLabel, FieldMessage, FieldTitle, FieldValue };
